import { useState, type FormEvent, type MouseEvent } from 'react';
import { KeyRoundIcon, LoaderCircleIcon } from 'lucide-react';
import { AuthError } from '@/server-threads';
import type { EntryCopy } from '@/i18n/ns/entry';
import { PasswordInput } from '@/components/ui/password-input';
import { EntryAlert, EntryField } from './entry-field';
import { EntryButton, EntryHeading } from './entry-shell';

export interface InviteGateCopy {
  verifying: string;
  confirmPasswordTitle: string;
  confirmPasswordBody: string;
  confirmPasswordPlaceholder: string;
  confirmPasswordSubmit: string;
  showPassword: string;
  hidePassword: string;
  errors: Record<string, string>;
}

export function VerificationCompleting({ copy }: { copy: InviteGateCopy }) {
  return (
    <div className="pc-entry-head" role="status">
      <div className="pc-entry-tile">
        <LoaderCircleIcon className="animate-spin motion-reduce:animate-none" aria-hidden="true" />
      </div>
      <p className="pc-entry-lead">{copy.verifying}</p>
    </div>
  );
}

/**
 * The verification link was opened outside the signup browser, so the HttpOnly
 * pending cookie can't vouch for it. The chosen password is the equivalent
 * proof: confirming it creates the account and logs in on THIS device. The
 * button stays enabled and the field is validated on submit, like every other
 * entry form.
 */
export function VerificationConfirmPassword({
  copy,
  entry,
  onConfirm,
}: {
  copy: InviteGateCopy;
  entry: EntryCopy;
  /** Re-submits verification with the password; throws AuthError on failure. */
  onConfirm: (password: string) => Promise<void>;
}) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event?: FormEvent | MouseEvent) => {
    event?.preventDefault();
    if (busy) return;
    if (!password) {
      setFieldError(entry.passwordRequired);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onConfirm(password);
    } catch (reason) {
      const code = reason instanceof AuthError ? reason.code : 'server_unavailable';
      // The server answers a wrong confirm password with 401 (bad_credentials);
      // in this screen there is no email field, so the message names the password.
      if (code === 'bad_credentials') setFieldError(copy.errors.verification_wrong_password);
      else setError(copy.errors[code] ?? copy.errors.generic);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <EntryHeading
        tile={<KeyRoundIcon aria-hidden="true" />}
        title={copy.confirmPasswordTitle}
        lead={copy.confirmPasswordBody}
      />
      <form className="pc-entry-form" noValidate onSubmit={(event) => void submit(event)}>
        <EntryField label={copy.confirmPasswordPlaceholder} error={fieldError}>
          {(control) => (
            <PasswordInput
              {...control}
              autoComplete="current-password"
              value={password}
              onChange={(event) => {
                setPassword(event.currentTarget.value);
                setFieldError(null);
                setError(null);
              }}
              className="pc-field-input"
              showLabel={copy.showPassword}
              hideLabel={copy.hidePassword}
            />
          )}
        </EntryField>
        {error ? <EntryAlert>{error}</EntryAlert> : null}
        <EntryButton
          type="submit"
          block
          busy={busy}
          busyLabel={entry.confirming}
          onClick={(event) => void submit(event)}
        >
          {copy.confirmPasswordSubmit}
        </EntryButton>
      </form>
    </>
  );
}
